// frontend/src/pages/DataWorkspacePage/DataStudio.jsx
/**
 * DataStudio — Column-first drag-and-drop canvas with full Live Workspace mode.
 *
 * Two states:
 *  'builder'  — Drag columns, resolve joins, configure filters.
 *  'live'     — Full-height editable AG Grid: Smartsheet-style.
 *
 * Wow features:
 *  - Named Saved Workspaces (save / open / delete)
 *  - Column stats popover (null %, unique, min/max, top values)
 *  - Conditional formatting rules per column
 *  - Auto-refresh (30s / 1m / 5m)
 *  - Export CSV + Excel (.xlsx)
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeBalham } from 'ag-grid-community';
import { apiFetch } from '../../api/index.js';
import { useNotification } from '../../context/NotificationContext.jsx';
import { DynamicIcon } from '../../components/icons.jsx';
import CommitPanel from './CommitPanel.jsx';

ModuleRegistry.registerModules([AllCommunityModule]);

// ─── AG Grid theme ───────────────────────────────────────────────────────────
const myTheme = themeBalham.withParams({
    backgroundColor: '#ffffff', oddRowBackgroundColor: '#f8fafc',
    headerBackgroundColor: '#f1f5f9', headerTextColor: '#475569',
    foregroundColor: '#1e293b', borderColor: '#e2e8f0',
    rowHoverColor: '#eff6ff', selectedRowBackgroundColor: '#e0f2fe',
    fontSize: '12px', cellHorizontalPadding: '12px',
    headerHeight: '36px', rowHeight: '32px',
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
const TYPE_COLOR = {
    int: 'bg-blue-50 text-blue-700 border-blue-200',
    float: 'bg-blue-50 text-blue-700 border-blue-200',
    decimal: 'bg-blue-50 text-blue-700 border-blue-200',
    numeric: 'bg-blue-50 text-blue-700 border-blue-200',
    date: 'bg-purple-50 text-purple-700 border-purple-200',
    time: 'bg-purple-50 text-purple-700 border-purple-200',
    bool: 'bg-amber-50 text-amber-700 border-amber-200',
    bit: 'bg-amber-50 text-amber-700 border-amber-200',
    default: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const getTypeColor = (t = '') => { const l = t.toLowerCase(); for (const k of Object.keys(TYPE_COLOR)) if (k !== 'default' && l.includes(k)) return TYPE_COLOR[k]; return TYPE_COLOR.default; };
const getTypeLabel = (t = '') => { const l = t.toLowerCase(); if (['int','float','decimal','numeric'].some(k => l.includes(k))) return '123'; if (['date','time'].some(k => l.includes(k))) return 'date'; if (['bool','bit'].some(k => l.includes(k))) return 'bool'; return 'abc'; };

const resolveJoin = (sourceTable, targetTable, schemaData) => {
    const src = schemaData.find(t => t.name === sourceTable);
    const tgt = schemaData.find(t => t.name === targetTable);
    if (tgt?.foreign_keys) { const fk = tgt.foreign_keys.find(f => f.referred_table === sourceTable); if (fk?.constrained_columns?.length) return { sourceCol: fk.referred_columns[0], targetCol: fk.constrained_columns[0], is_auto: true }; }
    if (src?.foreign_keys) { const fk = src.foreign_keys.find(f => f.referred_table === targetTable); if (fk?.constrained_columns?.length) return { sourceCol: fk.constrained_columns[0], targetCol: fk.referred_columns[0], is_auto: true }; }
    if (src?.columns && tgt?.columns) { for (const sc of src.columns) { const sn = sc.name.toLowerCase(); if (sn.endsWith('key') || sn.endsWith('id') || sn === 'id') { const m = tgt.columns.find(tc => tc.name === sc.name); if (m) return { sourceCol: sc.name, targetCol: m.name, is_auto: true }; } } }
    return null;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DataStudio = ({ token, schemaData, initialWorkspace, onSaved }) => {
    const { addNotification } = useNotification();
    const gridRef = useRef(null);

    // ── Core canvas state ────────────────────────────────────────────────────
    const [viewState,      setViewState]      = useState('builder'); // 'builder' | 'live'
    const [connectionId,   setConnectionId]   = useState(initialWorkspace?.connectionId || null);
    const [connectionName, setConnectionName] = useState(initialWorkspace?.connectionName || '');
    const [fields,         setFields]         = useState(initialWorkspace?.fields || []);
    const [joins,          setJoins]          = useState(initialWorkspace?.joins  || []);
    const [filters,        setFilters]        = useState(initialWorkspace?.filters || []);
    const [limit,          setLimit]          = useState(200);
    const [resolveModal,   setResolveModal]   = useState(null);
    const [isFieldDrop,    setIsFieldDrop]    = useState(false);

    // ── Result state ─────────────────────────────────────────────────────────
    const [previewData, setPreviewData] = useState(null);
    const [isLoading,   setIsLoading]   = useState(false);
    const [page,        setPage]        = useState(1);

    // ── Commit state ─────────────────────────────────────────────────────────
    const [diffs,      setDiffs]      = useState([]);
    const [showCommit, setShowCommit] = useState(false);

    // ── Workspace persistence ─────────────────────────────────────────────────
    const [savedWsId,       setSavedWsId]       = useState(initialWorkspace?.id || null);
    const [workspaceName,   setWorkspaceName]   = useState(initialWorkspace?.name || '');
    const [showSaveDialog,  setShowSaveDialog]  = useState(false);
    const [showMyDatasets,  setShowMyDatasets]  = useState(false);

    // ── Wow features state ───────────────────────────────────────────────────
    const [statsInfo,        setStatsInfo]        = useState(null);   // { column, data }
    const [statsLoading,     setStatsLoading]     = useState(false);
    const [condRules,        setCondRules]        = useState({});      // { col: { op, value, color } }
    const [showFormatCol,    setShowFormatCol]    = useState(null);
    const [autoRefreshSecs,  setAutoRefreshSecs]  = useState(null);   // null | 30 | 60 | 300
    const [showRefreshMenu,  setShowRefreshMenu]  = useState(false);

    const involvedTables = useMemo(() => [...new Set(fields.map(f => f.table))], [fields]);

    // ── Rehydrate when initialWorkspace changes (open from My Datasets) ───────
    useEffect(() => {
        if (!initialWorkspace) return;
        setConnectionId(initialWorkspace.connectionId || null);
        setConnectionName(initialWorkspace.connectionName || '');
        setFields(initialWorkspace.fields || []);
        setJoins(initialWorkspace.joins || []);
        setFilters(initialWorkspace.filters || []);
        setSavedWsId(initialWorkspace.id || null);
        setWorkspaceName(initialWorkspace.name || '');
        setPreviewData(null);
        setViewState('builder');
    }, [initialWorkspace]);

    // ── Auto-refresh ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!autoRefreshSecs || viewState !== 'live') return;
        const timer = setInterval(() => runPreview(page, true), autoRefreshSecs * 1000);
        return () => clearInterval(timer);
    }, [autoRefreshSecs, viewState, page]); // eslint-disable-line

    // ─── Fields Drop ─────────────────────────────────────────────────────────
    const handleFieldDragOver  = (e) => { e.preventDefault(); setIsFieldDrop(true); };
    const handleFieldDragLeave = ()  => setIsFieldDrop(false);

    const handleFieldDrop = useCallback((e) => {
        e.preventDefault();
        setIsFieldDrop(false);
        const raw = e.dataTransfer.getData('application/json');
        if (!raw) return;
        let payload; try { payload = JSON.parse(raw); } catch { return; }
        if (payload.type !== 'column') return;
        const { table, column, dataType, connectionId: cid, connectionName: cname } = payload;
        if (connectionId && cid !== connectionId) { addNotification('One connection at a time. Clear canvas to switch.', 'warning'); return; }
        if (fields.some(f => f.table === table && f.column === column)) return;
        if (!connectionId) { setConnectionId(cid); setConnectionName(cname); }
        setFields(prev => [...prev, { table, column, dataType }]);
        setJoins(prevJoins => {
            if (involvedTables.length === 0 || involvedTables.includes(table)) return prevJoins;
            if (prevJoins.some(j => j.sourceTable === table || j.targetTable === table)) return prevJoins;
            const baseTable = involvedTables[0];
            const resolved = resolveJoin(baseTable, table, schemaData);
            if (resolved) {
                addNotification(`Auto-joined ${table} ↔ ${baseTable} via ${resolved.sourceCol}`, 'success');
                return [...prevJoins, { sourceTable: baseTable, sourceCol: resolved.sourceCol, targetTable: table, targetCol: resolved.targetCol, joinType: 'LEFT', is_auto: true }];
            } else {
                setTimeout(() => setResolveModal({ sourceTable: baseTable, targetTable: table }), 100);
                return [...prevJoins, { sourceTable: baseTable, sourceCol: '', targetTable: table, targetCol: '', joinType: 'LEFT', is_auto: false, pending: true }];
            }
        });
    }, [connectionId, fields, involvedTables, schemaData, addNotification]);

    const removeField = (table, column) => {
        const next = fields.filter(f => !(f.table === table && f.column === column));
        setFields(next);
        if (!next.some(f => f.table === table)) setJoins(prev => prev.filter(j => j.sourceTable !== table && j.targetTable !== table));
        if (next.length === 0) { setConnectionId(null); setConnectionName(''); setJoins([]); setPreviewData(null); setDiffs([]); }
    };

    const clearCanvas = () => { setFields([]); setJoins([]); setFilters([]); setPreviewData(null); setConnectionId(null); setConnectionName(''); setDiffs([]); setSavedWsId(null); setWorkspaceName(''); setViewState('builder'); };

    // ─── Resolve Modal ────────────────────────────────────────────────────────
    const handleResolveConfirm = (st, sc, tt, tc, jt) => {
        setJoins(prev => prev.map(j => j.sourceTable === st && j.targetTable === tt ? { ...j, sourceCol: sc, targetCol: tc, joinType: jt, pending: false } : j));
        setResolveModal(null);
        addNotification(`Linked ${st}.${sc} → ${tt}.${tc}`, 'success');
    };

    // ─── Filters ─────────────────────────────────────────────────────────────
    const addFilter = ()            => setFilters(prev => [...prev, { column: '', operator: '=', value: '' }]);
    const updateFilter = (i, k, v)  => setFilters(prev => prev.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
    const removeFilter = (i)        => setFilters(prev => prev.filter((_, idx) => idx !== i));

    // ─── Execute ──────────────────────────────────────────────────────────────
    const runPreview = useCallback(async (pg = 1, silent = false) => {
        if (!connectionId || fields.length === 0) return;
        if (!silent) setIsLoading(true);
        setPage(pg);
        const sourceTable = involvedTables[0];
        const columns = fields.map(f => `${f.table}.${f.column}`);
        const activeJoins = joins.filter(j => j.sourceCol && j.targetCol && !j.pending).map(j => ({ table: j.targetTable, type: j.joinType, source_col: j.sourceCol, join_col: j.targetCol }));
        const activeFilters = filters.filter(f => f.column && f.value).map(f => ({ column: f.column, op: f.operator, value: f.value }));
        try {
            const res = await apiFetch('/data-workspace/workspaces/execute/ad-hoc', token, {
                method: 'POST',
                body: JSON.stringify({ connection_id: connectionId, workspace_json: { source: sourceTable, schema: null, columns, joins: activeJoins, filters: activeFilters, sort_col: '', sort_dir: 'ASC', limit }, page: pg, page_size: limit }),
            });
            setPreviewData(res);
            if (!silent) addNotification(`${(res.total || 0).toLocaleString()} rows loaded`, 'success');
        } catch (err) { addNotification(`Query failed: ${err.message}`, 'error'); }
        finally { if (!silent) setIsLoading(false); }
    }, [connectionId, fields, involvedTables, joins, filters, limit, token, addNotification]);

    const openLiveWorkspace = () => { runPreview(1); setViewState('live'); };

    // ─── Column Stats ─────────────────────────────────────────────────────────
    const fetchColumnStats = async (colFull) => {
        // colFull = "TableName.ColumnName"
        const parts = colFull.split('.');
        const table  = parts.length > 1 ? parts[0] : involvedTables[0];
        const column = parts.length > 1 ? parts[1] : parts[0];
        setStatsInfo({ column: colFull, data: null });
        setStatsLoading(true);
        try {
            const res = await apiFetch(`/data-workspace/connections/${connectionId}/query/column-stats`, token, {
                method: 'POST',
                body: JSON.stringify({ table_name: table, column_name: column, schema_name: null }),
            });
            setStatsInfo({ column: colFull, data: res });
        } catch (err) { addNotification(`Stats failed: ${err.message}`, 'error'); setStatsInfo(null); }
        finally { setStatsLoading(false); }
    };

    // ─── AG Grid col defs with conditional formatting ─────────────────────────
    const buildCellStyle = useCallback((colName) => (params) => {
        const rule = condRules[colName];
        if (!rule?.value) return {};
        const val = params.value;
        const rv = isNaN(rule.value) ? rule.value : Number(rule.value);
        let hit = false;
        if (rule.op === '>'  && Number(val) >  rv) hit = true;
        if (rule.op === '<'  && Number(val) <  rv) hit = true;
        if (rule.op === '>=' && Number(val) >= rv) hit = true;
        if (rule.op === '<=' && Number(val) <= rv) hit = true;
        if (rule.op === '='  && String(val) === String(rv)) hit = true;
        if (rule.op === '!=' && String(val) !== String(rv)) hit = true;
        return hit ? { backgroundColor: rule.color + '22', color: rule.color, fontWeight: '600' } : {};
    }, [condRules]);

    const colDefs = useMemo(() => {
        if (!previewData?.columns?.length) return [];
        return previewData.columns.map(c => ({
            field: c, headerName: c, editable: true, sortable: true, resizable: true, minWidth: 80, flex: 1,
            cellStyle: buildCellStyle(c),
            headerComponent: StatsHeaderCell,
            headerComponentParams: { colName: c, hasRule: !!condRules[c], onStats: fetchColumnStats, onFormat: () => setShowFormatCol(c) },
        }));
    }, [previewData?.columns, buildCellStyle, condRules]); // eslint-disable-line

    const rowData = useMemo(() => previewData?.rows || [], [previewData?.rows]);
    const totalPages = previewData ? Math.ceil(previewData.total / limit) : 0;

    const onCellValueChanged = useCallback((params) => {
        const { data, colDef, oldValue, newValue } = params;
        if (oldValue === newValue) return;
        setDiffs(prev => {
            const rk = JSON.stringify(data);
            const ei = prev.findIndex(d => JSON.stringify(d._rowSnapshot) === rk);
            if (ei >= 0) { const u = [...prev]; u[ei] = { ...u[ei], modified: { ...u[ei].modified, [colDef.field]: newValue } }; return u; }
            return [...prev, { _rowSnapshot: { ...data }, original: { ...data, [colDef.field]: oldValue }, modified: { ...data, [colDef.field]: newValue } }];
        });
        params.api?.flashCells({ rowNodes: [params.node], columns: [colDef.field] });
    }, []);

    // ─── Export ───────────────────────────────────────────────────────────────
    const exportCSV = () => gridRef.current?.api?.exportDataAsCsv({ fileName: `${workspaceName || 'export'}.csv` });

    const exportExcel = () => {
        if (!previewData?.columns?.length) return;
        // Build an HTML table that Excel opens natively — no dependency required
        const cols = previewData.columns;
        const header = cols.map(c => `<th>${c}</th>`).join('');
        const body   = rowData.map(row =>
            `<tr>${cols.map(c => `<td>${row[c] ?? ''}</td>`).join('')}</tr>`
        ).join('');
        const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:x="urn:schemas-microsoft-com:office:excel"
            xmlns="http://www.w3.org/TR/REC-html40">
            <head><meta charset="utf-8"/></head>
            <body><table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table></body>
        </html>`;
        const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `${workspaceName || 'export'}.xls`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ─── Save Workspace ───────────────────────────────────────────────────────
    const saveWorkspace = async (name, description) => {
        const ws_json = { connectionId, connectionName, fields, joins, filters };
        try {
            if (savedWsId) {
                await apiFetch(`/data-workspace/workspaces/${savedWsId}`, token, { method: 'PUT', body: JSON.stringify({ name, description, workspace_json: ws_json }) });
                addNotification(`"${name}" updated ✓`, 'success');
            } else {
                const res = await apiFetch('/data-workspace/workspaces', token, { method: 'POST', body: JSON.stringify({ name, description, connection_id: connectionId, workspace_json: ws_json }) });
                setSavedWsId(res.id);
                addNotification(`"${name}" saved ✓`, 'success');
            }
            setWorkspaceName(name);
            setShowSaveDialog(false);
            if (onSaved) onSaved();
        } catch (err) { addNotification(`Save failed: ${err.message}`, 'error'); }
    };

    const pendingJoins = joins.filter(j => j.pending);
    const canRun = connectionId && fields.length > 0 && pendingJoins.length === 0;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col h-full overflow-hidden bg-slate-50 relative">

            {/* ── Top Bar ──────────────────────────────────────────────────── */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-slate-200 shrink-0 gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <DynamicIcon name="Layers" className="w-4 h-4 text-violet-500 shrink-0" />
                    {viewState === 'live' ? (
                        <button onClick={() => setViewState('builder')} className="flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-700 transition-colors">
                            <DynamicIcon name="ChevronLeft" className="w-4 h-4" /> Edit Dataset
                        </button>
                    ) : (
                        <span className="text-sm font-semibold text-slate-800">Data Studio</span>
                    )}
                    {workspaceName && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 font-semibold truncate max-w-[160px]">{workspaceName}</span>}
                    {connectionName && !workspaceName && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{connectionName}</span>}
                    {fields.length > 0 && <span className="text-xs text-slate-400">{fields.length} field{fields.length !== 1 ? 's' : ''} · {involvedTables.length} table{involvedTables.length !== 1 ? 's' : ''}</span>}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {/* My Datasets */}
                    <button onClick={() => setShowMyDatasets(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-violet-600 bg-white border border-slate-200 hover:border-violet-300 rounded-lg transition-colors">
                        <DynamicIcon name="FolderOpen" className="w-3.5 h-3.5" /> My Datasets
                    </button>

                    {/* Save */}
                    {connectionId && (
                        <button onClick={() => setShowSaveDialog(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-emerald-600 bg-white border border-slate-200 hover:border-emerald-300 rounded-lg transition-colors">
                            <DynamicIcon name="Save" className="w-3.5 h-3.5" /> {savedWsId ? 'Update' : 'Save'}
                        </button>
                    )}

                    {/* Clear */}
                    {fields.length > 0 && (
                        <button onClick={clearCanvas} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-red-600 bg-white border border-slate-200 hover:border-red-200 rounded-lg transition-colors">
                            <DynamicIcon name="Trash2" className="w-3.5 h-3.5" /> Clear
                        </button>
                    )}

                    {viewState === 'live' && (
                        <>
                            {/* Auto-refresh */}
                            <div className="relative">
                                <button onClick={() => setShowRefreshMenu(p => !p)} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${autoRefreshSecs ? 'bg-emerald-50 text-emerald-700 border-emerald-300 animate-pulse' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                                    <DynamicIcon name="RefreshCw" className="w-3.5 h-3.5" />
                                    {autoRefreshSecs ? `${autoRefreshSecs}s` : 'Auto'}
                                </button>
                                {showRefreshMenu && (
                                    <div className="absolute top-9 right-0 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1 w-36" onMouseLeave={() => setShowRefreshMenu(false)}>
                                        {[['Off', null], ['30s', 30], ['1 min', 60], ['5 min', 300]].map(([label, val]) => (
                                            <button key={label} onClick={() => { setAutoRefreshSecs(val); setShowRefreshMenu(false); }} className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 transition-colors ${autoRefreshSecs === val ? 'text-violet-600 font-semibold' : 'text-slate-700'}`}>{label}</button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Export */}
                            <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                                <button onClick={exportCSV} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors border-r border-slate-200">
                                    <DynamicIcon name="Download" className="w-3.5 h-3.5" /> CSV
                                </button>
                                <button onClick={exportExcel} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                                    <DynamicIcon name="FileSpreadsheet" className="w-3.5 h-3.5" /> Excel
                                </button>
                            </div>
                        </>
                    )}

                    {/* Unsaved changes */}
                    {diffs.length > 0 && (
                        <button onClick={() => setShowCommit(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors animate-pulse shadow-sm">
                            <DynamicIcon name="AlertCircle" className="w-3.5 h-3.5" />
                            {diffs.length} unsaved
                        </button>
                    )}

                    {/* Limit + Run / Open Live */}
                    <div className="flex items-center gap-1.5 border-l border-slate-200 pl-3">
                        <input type="number" value={limit} onChange={e => setLimit(Number(e.target.value))} className="w-20 text-xs bg-white border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 focus:outline-none focus:border-violet-400 font-mono" />
                        {viewState === 'builder' ? (
                            <div className="flex items-center gap-1.5">
                                <button onClick={() => runPreview(1)} disabled={!canRun || isLoading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-slate-600 border border-slate-200 hover:border-violet-300 hover:text-violet-600 disabled:opacity-40 transition-colors">
                                    {isLoading ? <DynamicIcon name="Loader2" className="w-3.5 h-3.5 animate-spin" /> : <DynamicIcon name="Play" className="w-3.5 h-3.5" />}
                                    Run Preview
                                </button>
                                <button onClick={openLiveWorkspace} disabled={!canRun} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40 transition-colors shadow-sm">
                                    <DynamicIcon name="Maximize2" className="w-3.5 h-3.5" /> Open Live Workspace
                                </button>
                            </div>
                        ) : (
                            <button onClick={() => runPreview(1)} disabled={isLoading} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40 transition-colors shadow-sm">
                                {isLoading ? <DynamicIcon name="Loader2" className="w-3.5 h-3.5 animate-spin" /> : <DynamicIcon name="RefreshCw" className="w-3.5 h-3.5" />}
                                Refresh
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Builder View ─────────────────────────────────────────────── */}
            {viewState === 'builder' && (
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                    <div className="flex-none px-4 pt-3 pb-2 space-y-3 overflow-y-auto">
                        {/* Zone 1 — Fields Strip */}
                        <div onDragOver={handleFieldDragOver} onDragLeave={handleFieldDragLeave} onDrop={handleFieldDrop}
                            className={`min-h-[72px] rounded-xl border-2 border-dashed p-3 flex flex-wrap gap-2 items-center transition-all ${isFieldDrop ? 'border-violet-400 bg-violet-50 shadow-inner' : fields.length === 0 ? 'border-slate-300 bg-white' : 'border-slate-200 bg-white shadow-sm'}`}>
                            {fields.length === 0 ? (
                                <div className="w-full flex flex-col items-center justify-center text-slate-400 py-2 pointer-events-none gap-1">
                                    <DynamicIcon name="MousePointerClick" className="w-5 h-5" />
                                    <span className="text-xs font-medium">Expand a table in the sidebar → drag columns here to build your dataset</span>
                                </div>
                            ) : (
                                fields.map((f, i) => (
                                    <div key={`${f.table}.${f.column}.${i}`} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-mono font-medium shadow-sm ${getTypeColor(f.dataType)}`}>
                                        <span className="text-[10px] font-sans font-bold opacity-60">{getTypeLabel(f.dataType)}</span>
                                        <span className="opacity-50">{f.table}.</span><span>{f.column}</span>
                                        <button onClick={() => removeField(f.table, f.column)} className="ml-1 opacity-50 hover:opacity-100 transition-opacity"><DynamicIcon name="X" className="w-3 h-3" /></button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Zone 2 — Join Graph */}
                        {involvedTables.length > 1 && (
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3 space-y-2">
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Join Graph</p>
                                {joins.map((j, i) => (
                                    <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-xs ${j.pending ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                                        <span className="font-semibold text-slate-700">{j.sourceTable}</span>
                                        <DynamicIcon name="ArrowRight" className={`w-3.5 h-3.5 shrink-0 ${j.pending ? 'text-amber-500' : 'text-emerald-500'}`} />
                                        {j.pending ? (
                                            <>
                                                <span className="text-amber-700 font-medium flex-1">⚠ No keys found between {j.sourceTable} and {j.targetTable}</span>
                                                <button onClick={() => setResolveModal({ sourceTable: j.sourceTable, targetTable: j.targetTable })} className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-amber-500 hover:bg-amber-400 text-white shadow-sm transition-colors">Resolve →</button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="font-mono text-slate-600">{j.sourceCol}</span>
                                                <span className="text-slate-400">=</span>
                                                <span className="font-mono text-slate-600">{j.targetCol}</span>
                                                <DynamicIcon name="ArrowRight" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                                <span className="font-semibold text-slate-700">{j.targetTable}</span>
                                                {j.is_auto && <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 font-bold">AUTO</span>}
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${j.joinType === 'INNER' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{j.joinType}</span>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Filters */}
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><DynamicIcon name="Filter" className="w-3 h-3" />Filters</p>
                                <button onClick={addFilter} className="text-[10px] text-indigo-500 hover:text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded font-semibold transition-colors">+ Add Filter</button>
                            </div>
                            {filters.length === 0 ? <p className="text-xs text-slate-400 italic">No filters — showing all rows.</p> : (
                                <div className="flex flex-col gap-2">
                                    {filters.map((f, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">WHERE</span>
                                            <input placeholder="table.column" value={f.column} onChange={e => updateFilter(i, 'column', e.target.value)} className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 font-mono focus:outline-none focus:border-indigo-400" />
                                            <select value={f.operator} onChange={e => updateFilter(i, 'operator', e.target.value)} className="text-xs border border-slate-200 rounded px-1 py-1 font-mono font-bold focus:outline-none">
                                                {['=','!=','>','>=','<','<=','LIKE'].map(op => <option key={op}>{op}</option>)}
                                            </select>
                                            <input placeholder="value" value={f.value} onChange={e => updateFilter(i, 'value', e.target.value)} className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 font-mono text-emerald-700 focus:outline-none focus:border-indigo-400" />
                                            <button onClick={() => removeFilter(i)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"><DynamicIcon name="X" className="w-3.5 h-3.5" /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mini preview */}
                    <div className="flex-1 min-h-0 mx-4 mb-4 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden relative">
                        <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200 shrink-0">
                            <span className="text-xs text-slate-500">
                                {previewData ? <><b>{rowData.length}</b> of <b>{(previewData.total || 0).toLocaleString()}</b> rows (preview)</> : <span className="italic">Click "Open Live Workspace" to load full dataset</span>}
                            </span>
                            {previewData && (
                                <button onClick={openLiveWorkspace} className="flex items-center gap-1.5 px-3 py-1 text-[10px] font-semibold text-violet-600 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg transition-colors">
                                    <DynamicIcon name="Maximize2" className="w-3 h-3" /> Open Full Dataset
                                </button>
                            )}
                        </div>
                        <div className="flex-1 min-h-0">
                            {isLoading ? (
                                <div className="flex items-center justify-center h-full gap-2 text-slate-400"><DynamicIcon name="Loader2" className="w-5 h-5 animate-spin text-violet-500" /><span className="text-sm">Loading...</span></div>
                            ) : !previewData ? (
                                <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-300"><DynamicIcon name="BarChart3" className="w-10 h-10" /><span className="text-sm">Drop columns above and click Open Live Workspace</span></div>
                            ) : (
                                <AgGridReact ref={gridRef} theme={myTheme} rowData={rowData} columnDefs={colDefs} defaultColDef={{ sortable: true, resizable: true, filter: true }} enableCellChangeFlash suppressRowClickSelection rowSelection="multiple" animateRows onCellValueChanged={onCellValueChanged} />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Live Workspace View (Full Smartsheet Mode) ────────────────── */}
            {viewState === 'live' && (
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden relative">
                    {/* Grid toolbar */}
                    <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-slate-200 shrink-0">
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                            {isLoading ? <><DynamicIcon name="Loader2" className="w-3.5 h-3.5 animate-spin text-violet-500" /><span>Refreshing...</span></> : (
                                previewData && <span>Showing <b className="text-slate-800">{rowData.length.toLocaleString()}</b> of <b className="text-slate-800">{(previewData.total || 0).toLocaleString()}</b> rows</span>
                            )}
                            {autoRefreshSecs && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 text-[10px] font-semibold">↻ Auto {autoRefreshSecs}s</span>}
                        </div>
                        {previewData && (
                            <div className="flex items-center gap-1 text-xs text-slate-500">
                                <button onClick={() => runPreview(page - 1)} disabled={page <= 1} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 transition-colors"><DynamicIcon name="ChevronLeft" className="w-4 h-4" /></button>
                                <span className="px-2">Page <b>{page}</b> / <b>{totalPages || '—'}</b></span>
                                <button onClick={() => runPreview(page + 1)} disabled={page >= totalPages} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 transition-colors"><DynamicIcon name="ChevronRight" className="w-4 h-4" /></button>
                            </div>
                        )}
                    </div>

                    {/* Stats popover */}
                    {statsInfo && (
                        <StatsPopover info={statsInfo} loading={statsLoading} onClose={() => setStatsInfo(null)} />
                    )}

                    {/* Format rule dialog */}
                    {showFormatCol && (
                        <FormatRuleDialog
                            colName={showFormatCol}
                            existing={condRules[showFormatCol]}
                            onSave={(rule) => { setCondRules(prev => ({ ...prev, [showFormatCol]: rule })); setShowFormatCol(null); }}
                            onClear={() => { setCondRules(prev => { const n = { ...prev }; delete n[showFormatCol]; return n; }); setShowFormatCol(null); }}
                            onClose={() => setShowFormatCol(null)}
                        />
                    )}

                    {/* Full AG Grid */}
                    <div className="flex-1 w-full h-full min-h-0">
                        {!previewData ? (
                            <div className="flex items-center justify-center h-full gap-2 text-slate-400"><DynamicIcon name="Loader2" className="w-6 h-6 animate-spin text-violet-500" /></div>
                        ) : (
                            <AgGridReact
                                ref={gridRef}
                                theme={myTheme}
                                rowData={rowData}
                                columnDefs={colDefs}
                                defaultColDef={{ sortable: true, resizable: true, filter: true }}
                                onCellValueChanged={onCellValueChanged}
                                enableCellChangeFlash
                                suppressRowClickSelection
                                rowSelection="multiple"
                                animateRows
                            />
                        )}
                    </div>

                    {/* Floating Commit Bar */}
                    {diffs.length > 0 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 px-5 py-3 bg-[#0a0f1e] border border-amber-500/30 rounded-2xl shadow-2xl">
                            <DynamicIcon name="AlertCircle" className="w-4 h-4 text-amber-400" />
                            <span className="text-sm text-slate-200 font-medium">{diffs.length} unsaved change{diffs.length !== 1 ? 's' : ''}</span>
                            <button onClick={() => setDiffs([])} className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-200 transition-colors">Discard</button>
                            <button onClick={() => setShowCommit(true)} className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-white transition-colors">
                                <DynamicIcon name="GitCommit" className="w-3.5 h-3.5" /> Commit Changes
                            </button>
                        </div>
                    )}

                    {/* CommitPanel */}
                    {showCommit && (
                        <CommitPanel
                            token={token}
                            connectionId={connectionId}
                            tableName={involvedTables[0]}
                            schemaName={null}
                            diffs={diffs}
                            onClose={() => setShowCommit(false)}
                            onApplied={() => { setDiffs([]); setShowCommit(false); runPreview(page); }}
                        />
                    )}
                </div>
            )}

            {/* ── Modals ───────────────────────────────────────────────────── */}
            {resolveModal && <ResolveModal {...resolveModal} schemaData={schemaData} onConfirm={handleResolveConfirm} onDismiss={() => { setResolveModal(null); setJoins(prev => prev.filter(j => !(j.pending && j.sourceTable === resolveModal.sourceTable && j.targetTable === resolveModal.targetTable))); setFields(prev => prev.filter(f => f.table !== resolveModal.targetTable)); }} />}
            {showSaveDialog && <SaveWorkspaceDialog existingName={workspaceName} onSave={saveWorkspace} onClose={() => setShowSaveDialog(false)} />}
            {showMyDatasets && <MySavedDatasets token={token} onOpen={(ws) => { setShowMyDatasets(false); setConnectionId(ws.workspace_json?.connectionId || null); setConnectionName(ws.workspace_json?.connectionName || ''); setFields(ws.workspace_json?.fields || []); setJoins(ws.workspace_json?.joins || []); setFilters(ws.workspace_json?.filters || []); setSavedWsId(ws.id); setWorkspaceName(ws.name); setPreviewData(null); setViewState('builder'); addNotification(`Loaded "${ws.name}"`, 'success'); }} onClose={() => setShowMyDatasets(false)} />}
        </div>
    );
};

// ─── Stats Header Cell ────────────────────────────────────────────────────────
const StatsHeaderCell = ({ displayName, colName, hasRule, onStats, onFormat }) => (
    <div className="flex items-center gap-1 w-full pr-1">
        <span className="flex-1 truncate text-xs font-semibold text-slate-600">{displayName || colName}</span>
        <button onClick={() => onStats(colName)} title="Column stats" className="p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-blue-500 transition-colors shrink-0"><DynamicIcon name="BarChart2" className="w-3 h-3" /></button>
        <button onClick={() => onFormat(colName)} title="Format rules" className={`p-0.5 rounded hover:bg-slate-200 transition-colors shrink-0 ${hasRule ? 'text-amber-500' : 'text-slate-400 hover:text-amber-500'}`}><DynamicIcon name="Paintbrush" className="w-3 h-3" /></button>
    </div>
);

// ─── Stats Popover ────────────────────────────────────────────────────────────
const StatsPopover = ({ info, loading, onClose }) => (
    <div className="absolute top-12 right-4 z-30 w-72 bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-slate-200">
            <div className="flex items-center gap-2"><DynamicIcon name="BarChart2" className="w-4 h-4 text-blue-500" /><span className="text-sm font-bold text-slate-800 font-mono truncate">{info.column}</span></div>
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><DynamicIcon name="X" className="w-4 h-4" /></button>
        </div>
        {loading ? (
            <div className="flex items-center justify-center h-24 gap-2 text-slate-400"><DynamicIcon name="Loader2" className="w-4 h-4 animate-spin" /><span className="text-xs">Loading stats...</span></div>
        ) : info.data ? (
            <div className="p-4 space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                    {[['Total', info.data.total_rows?.toLocaleString()], ['Nulls', info.data.null_count?.toLocaleString()], ['Unique', info.data.unique_count?.toLocaleString()]].map(([label, val]) => (
                        <div key={label} className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</p>
                            <p className="text-sm font-bold text-slate-800">{val ?? '—'}</p>
                        </div>
                    ))}
                </div>
                {(info.data.min_val || info.data.max_val) && (
                    <div className="flex gap-2">
                        <div className="flex-1 bg-slate-50 rounded-lg p-2 border border-slate-100 text-center"><p className="text-[10px] text-slate-400">Min</p><p className="text-xs font-mono font-semibold text-slate-700 truncate">{info.data.min_val ?? '—'}</p></div>
                        <div className="flex-1 bg-slate-50 rounded-lg p-2 border border-slate-100 text-center"><p className="text-[10px] text-slate-400">Max</p><p className="text-xs font-mono font-semibold text-slate-700 truncate">{info.data.max_val ?? '—'}</p></div>
                    </div>
                )}
                {info.data.top_values?.length > 0 && (
                    <div><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Top Values</p>
                        {info.data.top_values.map((r, i) => (
                            <div key={i} className="flex items-center gap-2 py-0.5">
                                <span className="text-xs font-mono text-slate-600 flex-1 truncate">{String(r.val ?? '—')}</span>
                                <span className="text-[10px] text-slate-400">{r.cnt?.toLocaleString()}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        ) : null}
    </div>
);

// ─── Format Rule Dialog ───────────────────────────────────────────────────────
const RULE_COLORS = [['#ef4444','Red'],['#f97316','Orange'],['#eab308','Amber'],['#22c55e','Green'],['#3b82f6','Blue'],['#8b5cf6','Purple']];
const FormatRuleDialog = ({ colName, existing, onSave, onClear, onClose }) => {
    const [op, setOp]    = useState(existing?.op    || '>');
    const [val, setVal]  = useState(existing?.value  || '');
    const [color, setColor] = useState(existing?.color || '#ef4444');
    return (
        <div className="absolute top-12 right-4 z-30 w-72 bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-slate-200">
                <div className="flex items-center gap-2"><DynamicIcon name="Paintbrush" className="w-4 h-4 text-amber-500" /><span className="text-sm font-bold text-slate-800 font-mono truncate">{colName}</span></div>
                <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><DynamicIcon name="X" className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
                <p className="text-xs text-slate-500">Highlight cell when value:</p>
                <div className="flex gap-2">
                    <select value={op} onChange={e => setOp(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 font-mono font-bold focus:outline-none focus:border-amber-400 w-20">
                        {['=','!=','>','>=','<','<='].map(o => <option key={o}>{o}</option>)}
                    </select>
                    <input value={val} onChange={e => setVal(e.target.value)} placeholder="value" className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-400 font-mono" />
                </div>
                <div>
                    <p className="text-[10px] text-slate-400 mb-1.5">Highlight color:</p>
                    <div className="flex gap-2">
                        {RULE_COLORS.map(([c, label]) => (
                            <button key={c} title={label} onClick={() => setColor(c)} style={{ backgroundColor: c }} className={`w-6 h-6 rounded-full border-2 transition-all ${color === c ? 'border-slate-700 scale-125' : 'border-transparent'}`} />
                        ))}
                    </div>
                </div>
                <div className="flex gap-2 pt-1">
                    {existing && <button onClick={onClear} className="flex-1 text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg py-1.5 font-medium hover:bg-red-100 transition-colors">Clear Rule</button>}
                    <button onClick={() => onSave({ op, value: val, color })} disabled={!val} className="flex-1 text-xs text-white bg-amber-500 hover:bg-amber-400 rounded-lg py-1.5 font-semibold disabled:opacity-40 transition-colors">Apply</button>
                </div>
            </div>
        </div>
    );
};

// ─── Resolve Modal ────────────────────────────────────────────────────────────
const ResolveModal = ({ sourceTable, targetTable, schemaData, onConfirm, onDismiss }) => {
    const srcSchema = schemaData.find(t => t.name === sourceTable);
    const tgtSchema = schemaData.find(t => t.name === targetTable);
    const [srcCol, setSrcCol] = useState('');
    const [tgtCol, setTgtCol] = useState('');
    const [jt, setJt]         = useState('LEFT');
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-[480px] overflow-hidden">
                <div className="px-6 py-5 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-xl bg-amber-100 border border-amber-200 shrink-0"><DynamicIcon name="Link2Off" className="w-5 h-5 text-amber-600" /></div>
                        <div><h3 className="text-sm font-bold text-slate-800">How do these tables relate?</h3><p className="text-xs text-slate-500 mt-1">We couldn't automatically detect a relationship between <b>{sourceTable}</b> and <b>{targetTable}</b>. Pick the columns that link them.</p></div>
                    </div>
                </div>
                <div className="px-6 py-5 space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="flex-1 space-y-1">
                            <label className="text-[10px] font-semibold text-slate-500 uppercase">{sourceTable}</label>
                            <select value={srcCol} onChange={e => setSrcCol(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-violet-400 bg-white font-mono">
                                <option value="">Column...</option>
                                {(srcSchema?.columns || []).map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                            </select>
                        </div>
                        <div className="pt-5 text-slate-400"><DynamicIcon name="ArrowRightLeft" className="w-5 h-5" /></div>
                        <div className="flex-1 space-y-1">
                            <label className="text-[10px] font-semibold text-slate-500 uppercase">{targetTable}</label>
                            <select value={tgtCol} onChange={e => setTgtCol(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-violet-400 bg-white font-mono">
                                <option value="">Column...</option>
                                {(tgtSchema?.columns || []).map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-semibold text-slate-500 uppercase">Join Type</label>
                        <div className="flex gap-2">
                            {['LEFT','INNER','RIGHT'].map(t => (
                                <button key={t} onClick={() => setJt(t)} className={`flex-1 py-1.5 rounded-lg text-xs font-bold border transition-all ${jt === t ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'}`}>{t}</button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3">
                    <button onClick={onDismiss} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">Cancel</button>
                    <button onClick={() => onConfirm(sourceTable, srcCol, targetTable, tgtCol, jt)} disabled={!srcCol || !tgtCol} className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-semibold bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40 transition-colors shadow-sm">
                        <DynamicIcon name="Check" className="w-4 h-4" /> Confirm Join
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Save Workspace Dialog ────────────────────────────────────────────────────
const SaveWorkspaceDialog = ({ existingName, onSave, onClose }) => {
    const [name, setName]   = useState(existingName || '');
    const [desc, setDesc]   = useState('');
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-[400px] overflow-hidden">
                <div className="px-6 py-5 border-b border-slate-200 flex items-center gap-3">
                    <DynamicIcon name="Save" className="w-5 h-5 text-emerald-500" />
                    <h3 className="text-sm font-bold text-slate-800">Save Dataset</h3>
                </div>
                <div className="px-6 py-5 space-y-3">
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-slate-600">Name <span className="text-red-400">*</span></label>
                        <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Daily Charge Summary" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-emerald-400" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-slate-600">Description</label>
                        <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} placeholder="Optional description..." className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-emerald-400 resize-none" />
                    </div>
                </div>
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">Cancel</button>
                    <button onClick={() => onSave(name, desc)} disabled={!name.trim()} className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40 transition-colors shadow-sm">
                        <DynamicIcon name="Check" className="w-4 h-4" /> Save
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── My Saved Datasets Panel ──────────────────────────────────────────────────
const MySavedDatasets = ({ token, onOpen, onClose }) => {
    const { addNotification } = useNotification();
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading]       = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch('/data-workspace/workspaces', token);
                setWorkspaces(res || []);
            } catch (err) { addNotification(`Could not load datasets: ${err.message}`, 'error'); }
            finally { setLoading(false); }
        })();
    }, [token, addNotification]);

    const deleteWs = async (id, name) => {
        if (!window.confirm(`Delete "${name}"?`)) return;
        try {
            await apiFetch(`/data-workspace/workspaces/${id}`, token, { method: 'DELETE' });
            setWorkspaces(prev => prev.filter(w => w.id !== id));
            addNotification(`"${name}" deleted`, 'success');
        } catch (err) { addNotification(`Delete failed: ${err.message}`, 'error'); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-[560px] max-h-[70vh] flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2"><DynamicIcon name="FolderOpen" className="w-5 h-5 text-violet-500" /><h3 className="text-sm font-bold text-slate-800">My Saved Datasets</h3></div>
                    <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><DynamicIcon name="X" className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-24 gap-2 text-slate-400"><DynamicIcon name="Loader2" className="w-5 h-5 animate-spin" /><span className="text-sm">Loading...</span></div>
                    ) : workspaces.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-24 gap-2 text-slate-400">
                            <DynamicIcon name="FolderX" className="w-8 h-8" />
                            <span className="text-sm">No saved datasets yet. Build one in Data Studio and click Save!</span>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {workspaces.map(ws => {
                                const wj = ws.workspace_json || {};
                                const fieldCount = (wj.fields || []).length;
                                const tableCount = [...new Set((wj.fields || []).map(f => f.table))].length;
                                return (
                                    <div key={ws.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/30 transition-all group">
                                        <div className="p-2 rounded-lg bg-white border border-slate-200 shrink-0"><DynamicIcon name="Layers" className="w-4 h-4 text-violet-500" /></div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-800 truncate">{ws.name}</p>
                                            <p className="text-xs text-slate-500 truncate">{fieldCount} field{fieldCount !== 1 ? 's' : ''} · {tableCount} table{tableCount !== 1 ? 's' : ''} · Updated {new Date(ws.updated_at).toLocaleDateString()}</p>
                                        </div>
                                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => onOpen(ws)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors">
                                                <DynamicIcon name="FolderOpen" className="w-3.5 h-3.5" /> Open
                                            </button>
                                            <button onClick={() => deleteWs(ws.id, ws.name)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                                <DynamicIcon name="Trash2" className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DataStudio;
